/**
 * AssistantRoute — /assistent (Plan 05-07b, extended Plan 05-08a).
 *
 * Top-level route component for the Claude-powered family assistant.
 * Manages conversation state and the SSE stream lifecycle.
 *
 * Plan 05-08a extensions:
 * - RateLimitExhaustedState replaces InputRow when state.errorCode==='rate_limit'
 * - advisoryLine prop wired to MessageBubble for child+food-adjacent replies
 * - filterCorrected prop wired to MessageBubble from stream state
 *
 * - Empty state: SuggestedPrompts visible
 * - Active state: MessageThread with message history
 * - Streaming state: last assistant bubble has live cursor
 * - Rate-limited: InputRow hidden, RateLimitExhaustedState shown
 *
 * Per UI-SPEC §Interaction Model: Send → user bubble instant → assistant bubble
 * with cursor → SSE deltas append → done event removes cursor.
 *
 * #158 (steg 8a, Stell_Assistent):
 *   - Egen topp (`drawsOwnHeader`): ScreenHeader med tilbake til Hjem, den
 *     lime stjernen og «Assistent», og ⋯ (AssistantMenu) med «Tidligere
 *     samtaler», «Ny samtale» og (voksne) «Last opp ukeplan». Kvotechipen i skissen er utelatt; det finnes
 *     ikke noe endepunkt for den.
 *   - Mens en samtale pågår, står forslagene som chips over feltet
 *     (FollowUpPrompts). De skjules mens det ligger utkast til gjennomgang,
 *     så Utkast-flyten har bare «Lagre alle» over feltet.
 *   - Inntastingsraden har ingen flat bakgrunn lenger, så gløden synes.
 *
 * Idle-mode suppression (issue #29): `useHoldIdleWhileAnswering(state.status)`
 * holds the wall-tablet idle timer while a reply streams and for a grace
 * window afterwards, so the kitchen tablet no longer fades mid-answer. The
 * `inputFocused` half of the UI-SPEC rule is deliberately still unimplemented.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useAssistantStream } from './useAssistantStream';
import { useHoldIdleWhileAnswering } from './useHoldIdleWhileAnswering';
import { MessageThread, type ThreadMessage } from './MessageThread';
import { SuggestedPrompts } from './SuggestedPrompts';
import { InputRow } from './InputRow';
import { RateLimitExhaustedState } from './RateLimitExhaustedState';
import { StreamErrorNotice } from './StreamErrorNotice';
import { ConversationListSheet } from './ConversationListSheet';
import { VisionUploadSheet, type VisionSubmit } from './VisionUploadSheet';
import { DraftBatch } from './DraftBatch';
import { FollowUpPrompts } from './FollowUpPrompts';
import { AssistantMenu } from './AssistantMenu';
import { useVisionUpload, useDrafts, draftsKey } from './useVisionDrafts';
import { useConversationMessages } from './useConversations';
import { hydrateThreadMessages } from './hydrateThread';
import { useAdminMembers, ApiError, isAdminScopeError } from '../admin/useMembers';
import { AdminReauthModal } from '../admin/AdminReauthModal';
import { Icon, LoadingState } from '../../components/primitives';
import { ScreenHeader } from '../../components/ScreenHeader';
import { FeedbackWidget } from '../feedback/FeedbackWidget';
import { NAV_CLEARANCE, useWideViewport } from '../../lib/useViewport';

interface SessionMember {
  id: string;
  role: 'admin' | 'teen' | 'child';
  short_name: string;
}

type SessionRoleState =
  | { status: 'loading' }
  | { status: 'resolved'; role: 'admin' | 'teen' | 'child'; memberId: string }
  | { status: 'failed' };

const ACTIVE_CONVERSATION_STORAGE_KEY = 'stell.assistant.activeConversationId';

/**
 * Resolves the viewer's role from `/api/auth/me`. Fails **closed**: while
 * loading, or if the fetch throws or the response isn't ok, the caller must
 * treat the viewer as least-privileged — never assume `admin` just because
 * the request hasn't settled yet or didn't succeed.
 */
function useSessionRole(): SessionRoleState {
  const [state, setState] = useState<SessionRoleState>({ status: 'loading' });
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await globalThis.fetch('/api/auth/me', { credentials: 'same-origin' });
        if (!r.ok) {
          if (!cancelled) setState({ status: 'failed' });
          return;
        }
        const body = (await r.json()) as { member?: SessionMember };
        if (cancelled) return;
        if (body.member) {
          setState({ status: 'resolved', role: body.member.role, memberId: body.member.id });
        }
        else setState({ status: 'failed' });
      } catch {
        if (!cancelled) setState({ status: 'failed' });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return state;
}

export function AssistantRoute() {
  const { t } = useTranslation();
  const roleState = useSessionRole();
  // Least-privileged default: loading and failed both render as 'child' so
  // admin-only affordances (vision upload, DraftBatch) never flash before the
  // role is confirmed — see issue AI-05.
  const role = roleState.status === 'resolved' ? roleState.role : 'child';
  const memberId = roleState.status === 'resolved' ? roleState.memberId : null;
  const storageKey =
    memberId === null ? null : `${ACTIVE_CONVERSATION_STORAGE_KEY}:${memberId}`;
  const isAdmin = role === 'admin';
  // Skjermen fyller høyden selv og tegner sin egen topp (#158). Smal: trekk
  // fra pillen nederst, så inntastingsraden ikke havner bak den. Bred:
  // skinnen står til venstre og assistentknappen vises ikke her.
  const wide = useWideViewport();
  const qc = useQueryClient();
  const { state, start, cancel, resetConversation, restoreConversation } = useAssistantStream();
  // Issue #29 — keep the wall-tablet idle timer deferred while the answer is
  // streaming, and for a reading grace window after it lands.
  useHoldIdleWhileAnswering(state.status);
  const messagesQuery = useConversationMessages(state.conversationId ?? null);
  const { data: members } = useAdminMembers();
  const uploadMut = useVisionUpload();
  // Samme spørring som DraftBatch (delt cache); bare for å skjule forslagene
  // mens det ligger utkast til gjennomgang.
  const { data: drafts } = useDrafts(isAdmin ? state.conversationId : undefined);
  const hasPendingDrafts = (drafts ?? []).some((d) => d.status === 'pending');
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [listOpen, setListOpen] = useState(false);
  const [visionOpen, setVisionOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [hydrationTargetConversationId, setHydrationTargetConversationId] = useState<string | null>(null);
  const [reauth, setReauth] = useState<{ open: boolean; retry: (() => void) | null }>({ open: false, retry: null });
  const conversationGeneration = useRef(0);
  const restoredConversationForMember = useRef<string | null>(null);
  const isHydratingConversation =
    state.conversationId !== undefined && hydrationTargetConversationId === state.conversationId;

  useEffect(() => {
    if (memberId === null || storageKey === null) return;
    if (restoredConversationForMember.current !== memberId) return;
    try {
      if (state.conversationId) {
        globalThis.sessionStorage?.setItem(storageKey, state.conversationId);
      } else {
        globalThis.sessionStorage?.removeItem(storageKey);
      }
    } catch {
      // sessionStorage can be unavailable in some browser privacy modes.
    }
  }, [memberId, storageKey, state.conversationId]);

  useEffect(() => {
    if (memberId === null || storageKey === null) return;
    if (restoredConversationForMember.current === memberId) return;
    restoredConversationForMember.current = memberId;

    let savedId: string | null = null;
    try {
      savedId = globalThis.sessionStorage?.getItem(storageKey) ?? null;
    } catch {
      savedId = null;
    }
    const canRestore =
      messages.length === 0 &&
      state.status === 'idle' &&
      state.conversationId === undefined;
    if (!savedId || !canRestore) return;
    setHydrationTargetConversationId(savedId);
    restoreConversation(savedId);
  }, [memberId, storageKey, messages.length, state.status, state.conversationId, restoreConversation]);

  // En tur har noe å vise når den har tekst ELLER har rørt hukommelsen: et
  // notat kan være committet og `memory_changed` sendt selv om turen så
  // feiler eller ender uten tekst — da må brikken (og Angre) likevel stå
  // (review-runde 1 i !175, #193).
  const turnHasOutput = state.currentText !== '' || state.memoryChanges.length > 0;

  // When stream finishes or is cancelled, snapshot the assistant bubble into messages
  useEffect(() => {
    if ((state.status === 'done' || state.status === 'cancelled') && turnHasOutput) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: state.currentText,
          toolCalls: state.toolCalls,
          memoryChanges: state.memoryChanges,
          filterCorrected: state.filterCorrected,
          // advisoryLine is derived from foodAdjacent + role at render time; snapshot here
          advisoryLine: state.foodAdjacent === true && role === 'child',
          ts: new Date().toISOString(),
        },
      ]);
    }
  }, [state.status]);

  useEffect(() => {
    if (!state.conversationId) return;
    if (hydrationTargetConversationId !== state.conversationId) return;
    if (!messagesQuery.data?.messages) return;
    setMessages(hydrateThreadMessages(messagesQuery.data.messages, role));
    setHydrationTargetConversationId(null);
  }, [state.conversationId, hydrationTargetConversationId, messagesQuery.data, role]);

  // A stale/deleted stored id should not keep the assistant stuck on 404s.
  useEffect(() => {
    if (!state.conversationId) return;
    if (hydrationTargetConversationId !== state.conversationId) return;
    if (!messagesQuery.isError) return;
    setHydrationTargetConversationId(null);
    const isNotFoundError =
      messagesQuery.error instanceof Error &&
      messagesQuery.error.message === 'HTTP 404';
    if (!isNotFoundError) return;
    setMessages([]);
    resetConversation();
  }, [
    state.conversationId,
    hydrationTargetConversationId,
    messagesQuery.isError,
    messagesQuery.error,
    resetConversation,
  ]);

  const send = useCallback(
    (text: string) => {
      if (!text.trim() || state.status === 'streaming' || isHydratingConversation) return;
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: text, ts: new Date().toISOString() },
      ]);
      start({
        message: text,
        ...(state.conversationId !== undefined ? { conversationId: state.conversationId } : {}),
      });
    },
    [isHydratingConversation, state.status, state.conversationId, start],
  );

  const startNewConversation = useCallback(() => {
    conversationGeneration.current += 1;
    setMessages([]);
    setUploadError(null);
    setHydrationTargetConversationId(null);
    setListOpen(false);
    resetConversation();
  }, [resetConversation]);

  const openConversation = useCallback(
    (conversationId: string) => {
      conversationGeneration.current += 1;
      setUploadError(null);
      setMessages([]);
      setHydrationTargetConversationId(conversationId);
      // Intentional decision: opening another conversation aborts any in-flight
      // stream, then reloads only persisted rows from the selected thread.
      restoreConversation(conversationId);
      setListOpen(false);
    },
    [restoreConversation],
  );

  // Fase 6: when the photo turn finishes, the model has proposed drafts — refetch.
  useEffect(() => {
    if (state.status === 'done' && state.conversationId) {
      void qc.invalidateQueries({ queryKey: draftsKey(state.conversationId) });
    }
  }, [state.status, state.conversationId]);

  async function runUploadGuarded(
    fn: () => Promise<void>,
    submitGeneration: number,
  ): Promise<void> {
    setUploadError(null);
    try {
      await fn();
    } catch (err) {
      if (conversationGeneration.current !== submitGeneration) return;
      if (isAdminScopeError(err)) {
        setReauth({
          open: true,
          retry: () => void runUploadGuarded(fn, conversationGeneration.current),
        });
        return;
      }
      setUploadError(err instanceof ApiError ? err.code : 'unknown');
    }
  }

  function handleVisionSubmit(v: VisionSubmit): void {
    const submitGeneration = conversationGeneration.current;
    void runUploadGuarded(async () => {
      const uploaded = await uploadMut.mutateAsync(v.file);
      if (conversationGeneration.current !== submitGeneration) return;
      setMessages((prev) => [
        ...prev,
        { role: 'user', content: `${v.message} 📎`, ts: new Date().toISOString() },
      ]);
      start({
        message: v.message,
        image: { data: uploaded.data, media_type: uploaded.media_type },
        ...(v.assigneeId ? { assigneeId: v.assigneeId } : {}),
        ...(state.conversationId !== undefined ? { conversationId: state.conversationId } : {}),
      });
    }, submitGeneration);
  }

  const isEmpty = !isHydratingConversation && messages.length === 0 && state.status === 'idle';
  const isRateLimited = state.errorCode === 'rate_limit';
  // `aborted` is the user pressing Stop, not a failure — the partial reply is
  // already snapshotted into `messages` by the done/cancelled effect above.
  const showErrorNotice = state.status === 'error' && !isRateLimited;

  // Trailing bubble for the live turn. Also shown on error so an interrupted
  // half-answer stays on screen instead of vanishing — but without the cursor,
  // since nothing more is coming.
  const streamingMessage =
    state.status === 'streaming' || (state.status === 'error' && turnHasOutput)
      ? {
          role: 'assistant' as const,
          content: state.currentText,
          toolCalls: state.toolCalls,
          memoryChanges: state.memoryChanges,
          filterCorrected: state.filterCorrected,
          advisoryLine: state.foodAdjacent === true && role === 'child',
          isStreaming: state.status === 'streaming',
        }
      : null;

  /** Resend the last question. Does not append another user bubble. */
  const lastUserMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === 'user') return messages[i];
    }
    return undefined;
  }, [messages]);
  const retry =
    lastUserMessage !== undefined
      ? () =>
          start({
            message: lastUserMessage.content,
            ...(state.conversationId !== undefined
              ? { conversationId: state.conversationId }
              : {}),
          })
      : undefined;

  const promptRole = role === 'admin' ? 'parent' : role;

  return (
    <main
      data-testid="assistant-route"
      className="flex flex-col px-5 pt-4 md:px-8"
      style={{
        height: `calc(100dvh - ${wide ? 0 : NAV_CLEARANCE}px)`,
      }}
    >
      <ScreenHeader
        back={{ to: '/', testId: 'assistant-back' }}
        title={
          <span className="flex items-center gap-2.5">
            <span
              aria-hidden
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-fab text-on-fab"
            >
              <Icon.AssistantMark size={18} />
            </span>
            {t('assistant.header')}
          </span>
        }
        // #187: tilbakemelding ligger i ⋯ her, ikke som en knapp i toppen.
        hideFeedback
        trailing={
          <FeedbackWidget
            trigger={(openFeedback) => (
              <AssistantMenu
                onOpenConversations={() => setListOpen(true)}
                onNewConversation={startNewConversation}
                upload={
                  isAdmin
                    ? {
                        onOpen: () => setVisionOpen(true),
                        pending: uploadMut.isPending,
                        disabled: uploadMut.isPending || state.status === 'streaming',
                      }
                    : undefined
                }
                onFeedback={openFeedback}
              />
            )}
          />
        }
        className="pb-3"
      />

      {isHydratingConversation ? (
        <LoadingState lines={3} />
      ) : isEmpty ? (
        <SuggestedPrompts role={promptRole} onSelect={send} />
      ) : (
        <MessageThread
          messages={messages}
          streamingMessage={streamingMessage}
          footer={isAdmin ? <DraftBatch conversationId={state.conversationId} /> : undefined}
        />
      )}

      {showErrorNotice && (
        <StreamErrorNotice
          code={state.errorCode}
          serverMessage={state.errorMessage}
          fromServer={state.errorFromServer}
          onRetry={retry}
          onStartNewConversation={startNewConversation}
        />
      )}

      <ConversationListSheet
        open={listOpen}
        onClose={() => setListOpen(false)}
        onSelectConversation={openConversation}
      />

      {isAdmin && (
        <VisionUploadSheet
          open={visionOpen}
          onClose={() => setVisionOpen(false)}
          members={members ?? []}
          onSubmit={handleVisionSubmit}
        />
      )}

      <AdminReauthModal
        open={reauth.open}
        onClose={() => setReauth({ open: false, retry: null })}
        onSuccess={() => {
          const retry = reauth.retry;
          setReauth({ open: false, retry: null });
          retry?.();
        }}
      />

      {isRateLimited ? (
        <RateLimitExhaustedState
          role={role}
          serverMessage={state.errorFromServer ? state.errorMessage : undefined}
        />
      ) : isHydratingConversation ? (
        <div className="py-3">
          <LoadingState lines={1} />
        </div>
      ) : (
        // Ingen egen bakgrunn (#158): før var dette et flatt bg-paper-bånd
        // over gløden. Tråden over ruller i sin egen beholder, så raden
        // trenger ikke dekke noe.
        <div className="flex flex-col gap-2.5 pt-2 pb-3">
          {!isEmpty && !hasPendingDrafts && (
            <FollowUpPrompts
              role={promptRole}
              onSelect={send}
              disabled={state.status === 'streaming'}
            />
          )}
          {/* «Last opp ukeplan» ligger bak ⋯ (#158); feilen vises her. */}
          {isAdmin && uploadError && (
            <p role="alert" className="m-0 px-1 text-sm font-bold text-bad" data-testid="vision-upload-error">
              {t('assistant.vision.uploadError')}
            </p>
          )}
          <InputRow
            onSubmit={send}
            onStop={cancel}
            isStreaming={state.status === 'streaming'}
          />
        </div>
      )}
    </main>
  );
}
